import React, { useRef, useCallback, useMemo, useEffect, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";

import COMMON from "common";
import api from "services/api";
import sanitizeObject from "common/sanitize-object";
import serveRequestErrors from "common/serve-request-errors";
import AppEmptyState from "components/app-empty-state";
import AppSearchInput from "components/app-search-input";
import AppTable, { AppTableCell } from "components/app-table";
import addIcon from "assets/images/add-icon.svg";
import filterIcon from "assets/images/components/app-multi-select-input/filter-icon.svg";
import AppCreateAssignmentModal from "components/pages/e-learning/app-create-assignment-modal";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import * as yup from "yup";
import { useFormik } from "formik";
import queryParamsEntries from "common/query-params-entries";
import AppSelectInput from "components/app-select-input";
import ERRORS from "common/errors";
import { CALENDAR_FORMAT, formatDate, formatDatePattern, isDateBeforeToday, isEndDateBeforeStartDate } from "common/calendar";
import AppCalendarInput from "components/app-calendar-input";
import placeholderFormat from "common/placeholder-format";
import AppStatus from "components/app-status";
import AppUploadCertModal from "components/pages/e-learning/app-upload-cert-modal";

const calendarDisplayFormat = CALENDAR_FORMAT.DATE_FORMAT + " " + CALENDAR_FORMAT.MONTH_FORMAT + " " + CALENDAR_FORMAT.YEAR_FORMAT;

const PageOngoingAssignments = (props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const memoSetSearchParams = useRef(setSearchParams);
    const searchInputRef = useRef();
    const createAssignmentRef = useRef();
    const uploadCertificateRef = useRef();
    const paramsRef = useRef({ page: parseInt(searchParams.get("page")) || 1, sort: searchParams.get("sort") || "" });
    const currentYear = useMemo(() => new Date().getFullYear(), []);
    const minDate = useMemo(() => new Date(currentYear, 0, 1), [currentYear]);
    const [data, setData] = useState({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
    const cancelRequest = useMemo(() => props.onHandleCancelRequest, [props.onHandleCancelRequest]);
    const isEmptyState = useMemo(() => data.status === true, [data.status]);
    const initialValues = useMemo(() => ({ assignee: "", title: "", startDate: "", endDate: "", status: "" }), []);
    const formik = useFormik({
        initialValues,
        validationSchema: yup.object({
            startDate: yup.string().nullable().test("beforeEndDate", ERRORS.START_DATE, function (value) {
                if (!value || !this.parent.endDate) return true;
                return isDateBeforeToday(value, this.parent.endDate);
            }),
            endDate: yup.string().nullable().test("afterStartDate", ERRORS.END_DATE, function (value) {
                if (!value || !this.parent.startDate) return true;
                return isDateBeforeToday(this.parent.startDate || new Date(), value);
            }),
        }),
        onSubmit: (values) => {
            onHandleSubmitSearch(values);
        },
    });

    //prettier-ignore
    const onHandleStartDate = useCallback((name, value) => {
        formik.setFieldValue(name, value);

        if (isEndDateBeforeStartDate(value, formik.values.endDate)) formik.setFieldValue("endDate", "");
    }, [formik]);

    const onHandleSubmitSearch = (values) => {
        searchInputRef.current.onhandleCloseAdvanceSearch();

        paramsRef.current = { ...paramsRef.current, page: 1, ...values };

        onHandleGetList();
    };

    //prettier-ignore
    const onHandleResetSearch = () => {
        formik.setValues(formik.initialValues);

        paramsRef.current = { page: 1, sort: "", ...formik.initialValues };

        onHandleGetList();
    };

    const onHandleGetList = useCallback(async () => {
        let response = null;

        setData({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });

        try {
            const payload = { ...paramsRef.current, size: 10 };

            memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

            payload.page = paramsRef.current.page - 1;

            if (payload.startDate) {
                payload.startDate = formatDatePattern(new Date(payload.startDate), CALENDAR_FORMAT.CALENDAR_FORMAT);
            }

            if (payload.endDate) {
                payload.endDate = formatDatePattern(new Date(payload.endDate), CALENDAR_FORMAT.CALENDAR_FORMAT);
            }

            response = await api.get.assignment.ongoingList(sanitizeObject(payload));
        } catch (error) {
            serveRequestErrors(error);
        }

        if (response) {
            setData((prev) => ({
                ...prev,
                page: paramsRef.current.page,
                prev: !response.first,
                next: !response.last,
                items: response.content,
                total: response.totalPages,
                totalElements: response.totalElements,
                offset: response?.pageable?.offset || 0,
                status: response?.pageable,
            }));
        }
    }, []);

    //prettier-ignore
    const onHandlePagination = useCallback((event) => {
        const control = event.currentTarget?.getAttribute("data-ctrl");

        if (control) {
            if (control === "prev") {
                if (paramsRef.current.page <= 1) return;
                paramsRef.current.page -= 1;
            } else {
                if (paramsRef.current.page >= data.total) return;
                paramsRef.current.page += 1;
            }
        } else {
            paramsRef.current.page = event.target.value;
        }

        onHandleGetList();
    }, [onHandleGetList, data.total]);

    //prettier-ignore
    const onHandleSort = useCallback((id, order) => {
        paramsRef.current.sort = order ? id + "," + order : "";

        paramsRef.current.page = 1;

        onHandleGetList();
    }, [onHandleGetList]);

    const onHandleCreate = useCallback(() => {
        createAssignmentRef.current?.onHandleShow();
    }, []);

    //prettier-ignore
    const CourseCell = useCallback(({ row }) => {
        return (
            <div className="course-column">
                <AppTableCell left value={row.original.courseTitle} />
            </div>)
    }, []);

    //prettier-ignore
    const NameCell = useCallback(({ row }) => {
        return (
            <div className="name-column">
                <AppTableCell left value={row.original.employeeName} />
            </div>)
    }, []);

    //prettier-ignore
    const StatusCell = useCallback(({ row }) => {
        const status = row.original?.status;
        return (<AppStatus status={status ? status : "-"} />);
    }, []);

    //prettier-ignore
    const StartDateCell = useCallback(({ row }) => {
        const startDate = row.original?.startDate;
        return <AppTableCell center value={startDate ? formatDate(startDate) : "-"} />;
    }, [])

    //prettier-ignore
    const EndDateCell = useCallback(({ row }) => {
        const endDate = row.original?.endDate;
        return <AppTableCell center value={endDate ? formatDate(endDate) : "-"} />;
    }, [])

    //prettier-ignore
    const columns = useMemo(() => [
        {
            Header: "Assignee",
            accessor: "employeeName",
            disableSortBy: false,
            Cell: NameCell,
        },
        {
            Header: "Course",
            accessor: "courseTitle",
            disableSortBy: false,
            Cell: CourseCell,
        },
        {
            Header: "Start",
            accessor: "startDate",
            disableSortBy: false,
            Cell: StartDateCell,
        },
        {
            Header: "End",
            accessor: "endDate",
            disableSortBy: false,
            Cell: EndDateCell,
        },
        {
            Header: "Days Left",
            accessor: "daysLeft",
            disableSortBy: false,
        },
        {
            Header: "Template",
            accessor: "templateName",
            disableSortBy: true,
        },
        {
            Header: "Assigned By",
            accessor: "assignedByName",
            disableSortBy: false,
        },
        {
            Header: "Status",
            accessor: "status",
            disableSortBy: false,
            Cell: StatusCell,
        },
    ], [NameCell, StatusCell, StartDateCell, EndDateCell]);

    //prettier-ignore
    const onHandleRemoveField = (field) => {
        formik.setFieldValue(field, "");
        paramsRef.current = { ...paramsRef.current, page: 1, [field]: "" };
        onHandleGetList();
    };

    //prettier-ignore
    const advanceSearchValues = useMemo(() => {
        const params = queryParamsEntries(searchParams);
        let values = {
            assignee: params.assignee, title: params.title,
            startDate: params.startDate, endDate: params.endDate, status: params.status
        };

        return values;
    }, [searchParams]);

    //prettier-ignore
    const onHandleShowAdvanceSearch = (event) => {
        if (searchInputRef.current?.onHandleShowAdvanceSearch) {
            searchInputRef.current.onHandleShowAdvanceSearch(event);
        }
    };

    //prettier-ignore
    const headerButtons = useMemo(() => {
        const buttons = [
            { label: "Filter", icon: filterIcon, outline: true, onClick: onHandleShowAdvanceSearch },
            { label: "Add Assignment", icon: addIcon, onClick: onHandleCreate },
        ];
        return buttons;
    }, [onHandleCreate]);

    useEffect(() => {
        onHandleGetList();
    }, [onHandleGetList]);

    useEffect(() => {
        return () => {
            cancelRequest(COMMON.ENDPOINT_PATH.ASSIGNMENT.ONGOING_LIST);
        };
    }, [cancelRequest]);

    return (
        <div className="page-ongoing-assignments">
            <div className="ongoing-assignments">
                {isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" label="Add New Assignment" onClick={onHandleCreate} />}

                {!isEmptyState && (
                    <Fragment>
                        <AppSearchInput ref={searchInputRef} multiValues={advanceSearchValues} onRemoveField={onHandleRemoveField} buttons={headerButtons}>
                            <form className="app-advance-search-form" onSubmit={formik.handleSubmit}>
                                <div className="advance-form">
                                    <div className="advance-form__inputs">
                                        <AppInput type="text" name="assignee" label="Assignee Name" placeholder="Enter assignee name" value={formik.values.assignee} onChange={formik.handleChange} />

                                        <AppInput type="text" name="title" label="Course Title" placeholder="Enter course title" value={formik.values.title} onChange={formik.handleChange} />

                                        <AppCalendarInput name="startDate" label="Start Date" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} minDate={minDate} value={formik.values.startDate} error={formik.errors.startDate} touched={formik.touched.startDate} onChange={onHandleStartDate} />

                                        <AppCalendarInput name="endDate" label="End Date" placeholder={placeholderFormat} displayFormat={calendarDisplayFormat} minDate={formik.values.startDate || minDate} value={formik.values.endDate} error={formik.errors.endDate} touched={formik.touched.endDate} onChange={formik.setFieldValue} />

                                        <AppSelectInput name="status" label="Status" placeholder="Select Status" options={COMMON.DROPDOWNS.ONGOING_ASSIGNMENT} value={formik.values.status} onChange={formik.handleChange} />
                                    </div>

                                    <div className="advance-form__button-container">
                                        <AppButton type="button" label="Clear" outline onClick={onHandleResetSearch} />
                                        <AppButton type="submit" label="Search" />
                                    </div>
                                </div>
                            </form>
                        </AppSearchInput>

                        {(paramsRef.current.assignee || paramsRef.current.title || paramsRef.current.status
                            || paramsRef.current.startDate || paramsRef.current.endDate
                        ) && (
                                <div className="ongoing-assignments__results">
                                    <p className="ongoing-assignments__text">{data.totalElements} results found</p>
                                </div>
                            )}

                        <AppTable columns={columns} pages={data} onHandlePagination={onHandlePagination} onHandleSort={onHandleSort} />
                    </Fragment>
                )}
            </div>

            <AppCreateAssignmentModal ref={createAssignmentRef} onHandleGetList={onHandleGetList} />
            <AppUploadCertModal ref={uploadCertificateRef} onHandleGetList={onHandleGetList} />
        </div>
    );
};

export default PageOngoingAssignments;
